import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import '../css/EmailVerification.css';

const EmailVerification = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
    const [message, setMessage] = useState('');
    const [resendEmail, setResendEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendMessage, setResendMessage] = useState('');
    const hasVerifiedRef = useRef(false); // Use ref instead of state to prevent re-renders

    const token = searchParams.get('token');

    useEffect(() => {
        const verifyEmail = async () => {
            // Prevent multiple verification attempts using ref
            if (hasVerifiedRef.current || !token) {
                if (!token) {
                    setStatus('error');
                    setMessage('No verification token provided');
                }
                return;
            }

            hasVerifiedRef.current = true; // Mark as attempting verification

            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/users/verify-email`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ token }),
                    credentials: 'include'
                });

                const data = await response.json();

                if (response.ok) {
                    setStatus('success');
                    setMessage(data.message);
                    // Redirect to login after 3 seconds
                    setTimeout(() => {
                        navigate('/login');
                    }, 3000);
                } else {
                    setStatus('error');
                    setMessage(data.message);
                }
            } catch (error) {
                setStatus('error');
                setMessage('An error occurred while verifying your email');
            }
        };

        verifyEmail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]); // Only depend on token

    const handleResendVerification = async (e) => {
        e.preventDefault();
        if (!resendEmail) {
            setResendMessage('Please enter your email address');
            return;
        }

        setResendLoading(true);
        setResendMessage('');

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/users/resend-verification`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email: resendEmail }),
                credentials: 'include'
            });

            const data = await response.json();

            if (response.ok) {
                setResendMessage('Verification email sent successfully!');
                setResendEmail('');
            } else {
                setResendMessage(data.message);
            }
        } catch (error) {
            setResendMessage('An error occurred while sending the verification email');
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className="email-verification-container">
            <div className="verification-content">
                <div className="verification-card">
                    {status === 'verifying' && (
                        <>
                            <div className="spinner"></div>
                            <h2>Verifying your email...</h2>
                            <div className='loader'/>
                            <p>Please wait while we verify your email address.</p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <h2>Email Verified Successfully!</h2>
                            <p>{message}</p>
                            <p>You will be redirected to the login page in a few seconds...</p>
                            <button 
                                className="default-button"
                                onClick={() => navigate('/login')}
                                style={{ marginTop: '1vw' }}
                            >
                                Go to Login
                            </button>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <h2>Verification Failed</h2>
                            <p>{message}</p>
                            
                            <div className="resend-section">
                                <h3>Resend Verification Email</h3>
                                <form onSubmit={handleResendVerification}>
                                    <input
                                        type="email"
                                        placeholder="Enter your email address"
                                        value={resendEmail}
                                        onChange={(e) => setResendEmail(e.target.value)}
                                        className="input-field"
                                        required
                                    />
                                    <button 
                                        type="submit" 
                                        className="default-button"
                                        disabled={resendLoading}
                                    >
                                        {resendLoading ? 'Sending...' : 'Resend Email'}
                                    </button>
                                </form>
                                {resendMessage && (
                                    <p className={resendMessage.includes('successfully') ? 'success' : 'error'}>
                                        {resendMessage}
                                    </p>
                                )}
                            </div>

                            <button 
                                className="default-button"
                                onClick={() => navigate('/')}
                            >
                                Back to Home
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmailVerification;
